/**
 * **全局快捷键的注册与监听** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 快捷键**注册表**（`shortcuts`）留在宿主，这里管的是「怎么收键盘事件」：
 *
 *   `touchDevice`      是不是**真·触屏设备**（决定底部条显示快捷键还是手势指南）
 *   `visibleShortcuts` 过滤后的可见条目
 *   `onSpaceKey`       按住空格时**别让页面滚动**（编辑器里空格滚页面很讨厌）；
 *                      它**不再**是"临时平移"修饰键 —— 那个从没生效过，已删
 *   `onKeydown`        查表并执行
 *   `onMounted` / `onBeforeUnmount`  注册 / 摘掉 window 监听（含屏幕旋转时
 *                      跟着改 `touchDevice`）
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * `shortcuts` 的声明在宿主里、且比这段早，直接注入即可。
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { activeShortcuts, findShortcut, shouldIgnoreEvent } from '@/graph/shortcuts'
import type { Shortcut } from '@/graph/shortcuts'

export interface HotkeysCtx {
  /** 快捷键注册表（宿主里那个超长 computed） */
  shortcuts: { value: Shortcut[] }
  /** 空格是否按着（useSelection 的） */
}

export function useHotkeys(ctx: HotkeysCtx) {
  const { shortcuts } = ctx

/**
 * 是不是**真·触屏设备**（手机 / 平板）。
 *
 * ⚠️ 判据是 `(hover: none) and (pointer: coarse)`，**不是**单纯的
 * `(pointer: coarse)`。两者含义不同，混用会得到错的答案：
 *
 *   · `(pointer: coarse)`          —— 触屏笔记本的触摸屏也满足；
 *   · `(hover: none) and (pointer: coarse)` —— 只有**主输入方式既没有悬停
 *                                     又是粗指针**时才满足，也就是手机 / 平板。
 *
 * 这个 ref 的用途全都是**设备形态**判断（不是「按钮该多大」）：
 *   · 底部提示条显示快捷键还是手势指南
 *   · `panOnDrag` 用单指平移还是中键平移
 *   · 输出窗口贴底还是贴右侧
 *   · 调参面板要不要为输出窗口让位
 *
 * 「按钮该多大」是另一回事 —— 那个归 CSS 的 `(pointer: coarse)` 管
 * （见 theme.css 的 `--np-ctl-h-*`）。
 */
const touchDevice = ref(
  typeof window !== 'undefined' &&
    window.matchMedia?.('(hover: none) and (pointer: coarse)').matches === true,
)

const visibleShortcuts = computed(() =>
  activeShortcuts(shortcuts.value, touchDevice.value),
)

/**
 * 空格：**只做一件事 —— 别让页面滚动**。
 *
 * ⚠️ 它原来还兼职"临时平移修饰键"（写进 `spaceHeld`，再喂给
 * `useMiniMapGesture`）。但那条链**从来没生效过** —— 引擎那边要看的是
 * `panActivationKeyCode`，我们没传；实测反馈「空格 + 拖：没反应」。
 *
 * 所以 `spaceHeld` 连同注入链一起删了。**这个函数留着**：空格滚页面
 * 在一个满是画布的编辑器里只会让人误以为拖动生效了。
 */
function onSpaceKey(ev: KeyboardEvent) {
  if (ev.code !== 'Space' || shouldIgnoreEvent(ev)) return
  ev.preventDefault()
}

function onKeydown(ev: KeyboardEvent) {
  // 在输入框里打字时不触发 —— 否则在「名称」里按 Ctrl+H 会把端口藏了
  if (shouldIgnoreEvent(ev)) return

  const hit = findShortcut(shortcuts.value, ev)
  if (!hit?.run) return
  ev.preventDefault()
  hit.run()
}

onMounted(() => {
  /*
   * 屏幕旋转 / 接上鼠标时跟着变。
   * ⚠️ 监听的查询必须和上面初始化用的**是同一个** —— 否则会出现
   * 「初值按 A 算、变化按 B 算」的错位（旧代码就是这么写的：
   * 初值 `(pointer: coarse)`，变化也 `(pointer: coarse)`，
   * 而用户期望的是 `(hover: none) and (pointer: coarse)`）。
   */
  const mq = window.matchMedia?.('(hover: none) and (pointer: coarse)')
  mq?.addEventListener?.('change', () => {
    touchDevice.value = mq?.matches === true
  })

  window.addEventListener('keydown', onKeydown)
  window.addEventListener('keydown', onSpaceKey)
  window.addEventListener('keyup', onSpaceKey)

})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('keydown', onSpaceKey)
  window.removeEventListener('keyup', onSpaceKey)
})


  return { touchDevice, visibleShortcuts, onSpaceKey }
}
